import { Routes } from '@angular/router';

import { userResolver } from './shared/resolvers/user-resolve.service';
import { NotePurchaseStatusResolve } from './shared/resolvers/note-purchase-status-resolve.service';
import { streamDetailsResolver, streamListResolver } from './shared/resolvers/stream-resolve.service';
import { NoteResolve } from './shared/resolvers/note-resolve.service';
import { loginRedirectGuard } from './shared/route-guards/login-redirect.guard';
import { UserStreamsRedirectGuard } from './shared/route-guards/user-streams-redirect.guard';
import { NoteDraftGuard } from './shared/route-guards/note-draft.guard';
import { StreamIdGuard } from './shared/route-guards/stream-id.guard';
import { NoteGuideGuard } from './shared/route-guards/note-guide.guard';
import { GuardQueue } from './shared/route-guards/guard-queue.quard';
import { authGuard } from './shared/route-guards/auth.guard';
import { NoteOwnerGuard } from './shared/route-guards/note-owner.guard';
import { userRoutes } from './user/user.routes';
import { confirmNoteDeactivateGuard } from './shared/route-guards/note-dectivate.quard';
import { streamOwnerGuard } from './shared/route-guards/stream-owner.guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./landing/landing.component').then(c => c.LandingComponent),
    pathMatch: 'full',
    resolve: { user: userResolver }
  },
  {
    path: 'm',
    resolve: { user: userResolver },
    children: [
      { path: 'latest', loadComponent: () => import('./landing/landing.component').then(c => c.LandingComponent), data: { mode: 'latest' } },
      { path: 'popular', loadComponent: () => import('./landing/landing.component').then(c => c.LandingComponent), data: { mode: 'popular' } }
    ]
  },
  {
    path: 'why-publish',
    loadComponent: () => import('./why-publish/why-publish.component').then(c => c.WhyPublishComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'why-publish:query',
    loadComponent: () => import('./why-publish/why-publish.component').then(c => c.WhyPublishComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'how-notd-works',
    loadComponent: () => import('./how-notd-works/how-notd-works.component').then(c => c.HowNotdWorksComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'home',
    loadComponent: () => import('./home/home.component').then(c => c.HomeComponent),
    resolve: { user: userResolver, streams: streamDetailsResolver }
  },
  {
    path: 'about',
    loadComponent: () => import('./about/about-us.component').then(c => c.AboutUsComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'sitemap',
    loadComponent: () => import('./sitemap/sitemap.component').then(c => c.SitemapComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'faq',
    loadComponent: () => import('./faq/faq.component').then(c => c.FaqComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'ci-test',
    loadComponent: () => import('./ci-test/ci-test.component').then(c => c.CiTestComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'how-to-use-podcast',
    loadComponent: () => import('./how-to-use-podcast/how-to-use-podcast.component').then(c => c.HowToUsePodcastComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'terms',
    loadComponent: () => import('./terms/terms.component').then(c => c.TermsComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./privacy-policy/privacy-policy.component').then(c => c.PrivacyPolicyComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'notes/publish-thank-you/:id',
    loadComponent: () => import('./publish-thank-you/publish-thank-you.component').then(c => c.PublishThankYouComponent),
    resolve: { note: NoteResolve, user: userResolver },
    canActivate: [NoteOwnerGuard]
  },
  {
    path: 'notes/:id',
    resolve: { note: NoteResolve, user: userResolver, noteStatus: NotePurchaseStatusResolve },
    canActivate: [NoteDraftGuard],
    children: [
      {
        path: 'publish-thank-you',
        loadComponent: () => import('./publish-thank-you/publish-thank-you.component').then(c => c.PublishThankYouComponent),
        canActivate: [NoteOwnerGuard]
      },
      {
        path: ':name',
        loadComponent: () => import('./note-details/note-details.component').then(c => c.NoteDetailsComponent)
      }
    ]
  },
  {
    path: 'n/:custom-url',
    loadComponent: () => import('./note-details/note-details.component').then(c => c.NoteDetailsComponent),
    resolve: { note: NoteResolve, user: userResolver, noteStatus: NotePurchaseStatusResolve },
    canActivate: [NoteDraftGuard]
  },
  {
    path: 'thread/:id',
    loadComponent: () => import('./thread/thread.component').then(c => c.ThreadComponent),
    resolve: { user: userResolver, note: NoteResolve }
  },
  {
    path: 'note',
    loadComponent: () => import('./shared/note-detailed/creator/note-creator.component').then(c => c.NoteCreatorComponent),
    resolve: { user: userResolver },
    canActivate: [UserStreamsRedirectGuard],
    canDeactivate: [confirmNoteDeactivateGuard]
  },
  {
    path: 'note/:id',
    loadComponent: () => import('./shared/note-detailed/creator/note-creator.component').then(c => c.NoteCreatorComponent),
    resolve: { user: userResolver },
    canActivate: [loginRedirectGuard],
    canDeactivate: [confirmNoteDeactivateGuard]
  },
  {
    path: 'new-note-guide',
    loadComponent: () => import('./create-note-guide/create-note-guide.component').then(c => c.CreateNoteGuideComponent),
    canActivate: [GuardQueue],
    data: { guards: [loginRedirectGuard, authGuard, NoteGuideGuard] }
  },
  {
    path: 'stream/:streamId/member/invite/accept/:invitationToken',
    loadComponent: () => import('./streams/stream-community-invitation/stream-community-invitation.component').then(c => c.StreamCommunityInvitationComponent),
    canActivate: [loginRedirectGuard],
    resolve: { user: userResolver }
  },
  {
    path: 'streams/:id/view/:myStreamName',
    loadComponent: () => import('./streams/stream-details/stream-details.component').then(c => c.StreamDetailsComponent),
    resolve: { user: userResolver, requestedStream: streamDetailsResolver },
    canActivate: [StreamIdGuard]
  },
  {
    path: 'streams/:id/edit',
    loadComponent: () => import('./streams/stream-edit-view/stream-edit-view.component').then(c => c.StreamEditViewComponent),
    resolve: { user: userResolver, stream: streamDetailsResolver },
    canActivate: [streamOwnerGuard]
  },
  {
    path: 's/:custom-url/edit',
    loadComponent: () => import('./streams/stream-edit-view/stream-edit-view.component').then(c => c.StreamEditViewComponent),
    resolve: { user: userResolver, stream: streamDetailsResolver },
    canActivate: [StreamIdGuard, streamOwnerGuard]
  },
  {
    path: 'streams/create',
    loadComponent: () => import('./streams/stream-create/stream-create.component').then(c => c.StreamCreateComponent),
    canActivate: [GuardQueue],
    resolve: { user: userResolver },
    data: { guards: [loginRedirectGuard, authGuard] }
  },
  {
    path: 'streams/:id/owner',
    canActivate: [streamOwnerGuard],
    resolve: { user: userResolver, requestedStream: streamDetailsResolver },
    children: [
      { path: 'profile', loadComponent: () => import('./streams/stream-details/stream-details.component').then(c => c.StreamDetailsComponent), resolve: { user: userResolver, requestedStream: streamDetailsResolver }, data: { mode: 'profile' } },
      { path: 'subscription/settings', loadComponent: () => import('./streams/stream-details/stream-details.component').then(c => c.StreamDetailsComponent), resolve: { user: userResolver, requestedStream: streamDetailsResolver }, data: { mode: 'subscription/settings' } },
      { path: 'analytics/page-views', loadComponent: () => import('./streams/stream-details/stream-details.component').then(c => c.StreamDetailsComponent), resolve: { user: userResolver, requestedStream: streamDetailsResolver }, data: { mode: 'analytics/page-views' } },
      { path: '**', redirectTo: '' }
    ]
  },
  {
    path: 's/:custom-url',
    loadComponent: () => import('./streams/stream-details/stream-details.component').then(c => c.StreamDetailsComponent),
    resolve: { user: userResolver, requestedStream: streamDetailsResolver },
    canActivate: [StreamIdGuard]
  },
  {
    path: 'streams/:id',
    loadComponent: () => import('./streams/stream-details/stream-details.component').then(c => c.StreamDetailsComponent),
    resolve: { user: userResolver, requestedStream: streamDetailsResolver },
    canActivate: [StreamIdGuard]
  },
  {
    path: 'streams',
    loadComponent: () => import('./streams/stream-base/stream-base.component').then(c => c.StreamBaseComponent),
    canActivate: [loginRedirectGuard],
    resolve: { ownedStreams: streamListResolver }
  },
  {
    path: 'popular',
    resolve: { user: userResolver },
    children: [
      { path: '', loadComponent: () => import('./popular/popular.component').then(c => c.PopularComponent) },
      { path: ':input', loadComponent: () => import('./popular/popular.component').then(c => c.PopularComponent) }
    ]
  },
  {
    path: 't',
    resolve: { user: userResolver },
    children: [
      { path: ':teamId', loadComponent: () => import('./team-view/team-view.component').then(c => c.TeamViewComponent) },
      { path: ':teamId/:teamName', loadComponent: () => import('./team-view/team-view.component').then(c => c.TeamViewComponent) }
    ]
  },
  {
    path: 'team/:teamId/member/invite/accept/:invitationToken',
    loadComponent: () => import('./team/team-invitation/team-invitation.component').then(c => c.TeamInvitationComponent),
    canActivate: [loginRedirectGuard],
    resolve: { user: userResolver }
  },
  {
    path: 'team',
    loadComponent: () => import('./team/team.component').then(c => c.TeamComponent),
    canActivate: [loginRedirectGuard],
    resolve: { user: userResolver }
  },
  {
    path: 'search/:query',
    loadComponent: () => import('./search/search.component').then(c => c.SearchComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'search/query/:type/:query',
    loadComponent: () => import('./search/search.component').then(c => c.SearchComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'search/:form/:category',
    loadComponent: () => import('./search/search.component').then(c => c.SearchComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'search/:form/:category/:type',
    loadComponent: () => import('./search/search.component').then(c => c.SearchComponent),
    resolve: { user: userResolver }
  },
  {
    path: 'search/:form/:category/:type/:query',
    loadComponent: () => import('./search/search.component').then(c => c.SearchComponent),
    resolve: { user: userResolver },
    pathMatch: 'full'
  },
  {
    path: 'info/signup-thankyou',
    loadComponent: () => import('./info/signup-thankyou/signup.component').then(c => c.SignupThankYouComponent),
    resolve: { user: userResolver }
  },
  {
    path: '404',
    loadComponent: () => import('./not-found/not-found.component').then(c => c.NotFoundComponent),
    resolve: { user: userResolver },
    pathMatch: 'full'
  },
  ...userRoutes,
  { path: '**', redirectTo: '' }
];